/** @format */
import { Inject, Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    CurriculumPeriod,
    DegreeProgrammeAttainment,
    Module,
    ModuleAttainment,
    OtmId,
    Plan,
    StudyRight,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    COMMON_PLAN_SELECTION_SERVICE,
    COMMON_PLAN_SERVICE,
    convertAJSPromiseToNative,
    CurriculumPeriodEntityService,
    PlanEntityService,
    RawPlanEditService,
} from 'sis-components';
import { UI_CONTEXT } from '../../../ajs-upgraded-modules';
import { PlanCopyService } from '../../service/plan-copy.service';

@Injectable({
    providedIn: 'root',
})
export class ModuleInfoService {
    constructor(
        @Inject(UI_CONTEXT) private uiContext: any,
        @Inject(COMMON_PLAN_SELECTION_SERVICE) private commonPlanSelectionService: any,
        @Inject(COMMON_PLAN_SERVICE) private commonPlanService: any,
        private planEntityService: PlanEntityService,
        private rawPlanEditService: RawPlanEditService,
        private planCopyService: PlanCopyService,
        private curriculumPeriodEntityService: CurriculumPeriodEntityService,
    ) {}

    /**
     * Version ottaminen käyttöön suunnitelmassa on mahdollista silloin:
     * - jos versio on kiinni opintosuunnitelmalle valitussa opetussuunnitelmakaudessa
     * - tai sitä uudemmassa kaudessa
     * - jos opiskelijalla on suoritettuna kyseinen versio.
     *
     * @param moduleVersions - The Module versions
     * @param attainments - list of valid student attainments
     * @returns Compatible module id list
     */
    getModuleVersionCompatibility(moduleVersions: Module[], attainments: Attainment[]): Observable<OtmId[]> {
        return this.getModuleVersionCompatibilityForPlan(moduleVersions, attainments, this.uiContext.planContext.curriculumPeriodId);
    }

    /**
     * Param-based variant of {@link getModuleVersionCompatibility} for the decoupled StudentModuleInfoModalComponent,
     * which has no AngularJS uiContext. The plan curriculum period is passed in explicitly
     * (`validatablePlan.plan.curriculumPeriodId`).
     */
    getModuleVersionCompatibilityForPlan(
        moduleVersions: Module[],
        attainments: Attainment[],
        planCurriculumPeriodId: OtmId,
    ): Observable<OtmId[]> {
        const compatibleModuleIds = new Set<OtmId>();
        const attainedModuleIds = new Set(
            (
                attainments.filter(att => att.type === 'DegreeProgrammeAttainment' || att.type === 'ModuleAttainment') as (
                    | DegreeProgrammeAttainment
                    | ModuleAttainment
                )[]
            ).map(att => att.moduleId),
        );

        const moduleCurriculumPeriodIds = new Set(moduleVersions.flatMap(moduleVersion => moduleVersion.curriculumPeriodIds));
        return this.curriculumPeriodEntityService.getByIds([...new Set([planCurriculumPeriodId, ...moduleCurriculumPeriodIds])]).pipe(
            map(curriculumPeriods => {
                const curriculumPeriodsById = new Map<OtmId, CurriculumPeriod>();
                for (const curriculumPeriod of curriculumPeriods) {
                    curriculumPeriodsById.set(curriculumPeriod.id, curriculumPeriod);
                }
                for (const moduleVersion of moduleVersions) {
                    // Add attained module version ids to the set
                    if (attainedModuleIds.has(moduleVersion.id)) {
                        compatibleModuleIds.add(moduleVersion.id);
                    }
                    // Add module version if some curriculum period matches the plan curriculum period.
                    if (moduleVersion.curriculumPeriodIds.some(curriculumPeriodId => curriculumPeriodId === planCurriculumPeriodId)) {
                        compatibleModuleIds.add(moduleVersion.id);
                    }
                    // Add module version if it has some curriculum period that is after the plan curriculum period
                    if (
                        moduleVersion.curriculumPeriodIds.some(curriculumPeriodId => {
                            if (!curriculumPeriodsById.has(curriculumPeriodId) || !curriculumPeriodsById.has(planCurriculumPeriodId)) {
                                return false;
                            }
                            const curriculumPeriodStartDate = new Date(
                                curriculumPeriodsById.get(curriculumPeriodId).activePeriod.startDate,
                            );
                            const planCurriculumPeriodStartDate = new Date(
                                curriculumPeriodsById.get(planCurriculumPeriodId).activePeriod.startDate,
                            );
                            return curriculumPeriodStartDate > planCurriculumPeriodStartDate;
                        })
                    ) {
                        compatibleModuleIds.add(moduleVersion.id);
                    }
                }
                return [...compatibleModuleIds];
            }),
        );
    }

    copyPlanAndReplaceModule(oldModule: Module, newModule: Module): Observable<Plan> {
        if (!this.uiContext.hasActivePlan()) {
            return throwError(() => 'No active plan');
        }
        return from(convertAJSPromiseToNative(this.uiContext.planContext.toPlan())).pipe(
            switchMap((plan: Plan) =>
                this.copyPlanAndReplaceModuleInternal(
                    oldModule,
                    newModule,
                    plan,
                    this.uiContext.planContext.validatablePlan,
                    this.uiContext.planContext?.studyRight,
                ),
            ),
        );
    }

    /**
     * Param-based variant of {@link copyPlanAndReplaceModule} for the decoupled StudentModuleInfoModalComponent.
     * Creates a new plan (copy) with the module version swapped and persists it via createMyPlan.
     */
    copyPlanAndReplaceModuleForPlan(
        oldModule: Module,
        newModule: Module,
        validatablePlan: ValidatablePlan,
        studyRight: StudyRight,
    ): Observable<Plan> {
        return this.copyPlanAndReplaceModuleInternal(oldModule, newModule, validatablePlan.plan, validatablePlan, studyRight);
    }

    replaceModule(oldModule: Module, newModule: Module): Observable<Module> {
        const oldModuleParentModule =
            this.uiContext.planContext.validatablePlan.getParentModuleOrCustomModuleAttainmentForModule(oldModule);
        const studyRight = this.uiContext.planContext?.studyRight;

        return from(convertAJSPromiseToNative(this.uiContext.planContext.forceRemoveModule(oldModule, oldModuleParentModule))).pipe(
            switchMap(() =>
                from(
                    convertAJSPromiseToNative(
                        this.uiContext.planContext.selectModule(
                            oldModuleParentModule,
                            newModule,
                            this.uiContext.planContext.validatablePlan.plan,
                            this.uiContext.planContext.validatablePlan,
                            studyRight,
                        ),
                    ),
                ),
            ),
            map(() => this.uiContext.planContext.getModule(newModule.id)),
        );
    }

    /**
     * Param-based, in-place variant of {@link replaceModule} for the decoupled StudentModuleInfoModalComponent.
     * There is no live planContext to auto-persist, so the (cloned) plan is mutated and persisted via updateMyPlan.
     * Returns the persisted plan.
     */
    replaceModuleForPlan(oldModule: Module, newModule: Module, validatablePlan: ValidatablePlan, studyRight: StudyRight): Observable<Plan> {
        const parentModule = validatablePlan.getParentModuleOrCustomModuleAttainmentForModule(oldModule);
        // Deep clone so the open modal's validatablePlan is never mutated (a failed save must leave the UI intact).
        const rawPlan: Plan = _.cloneDeep(validatablePlan.plan);
        this.rawPlanEditService.removeModuleRecursively(oldModule.id, rawPlan);
        return from(convertAJSPromiseToNative(this.commonPlanService.getValidatablePlan(rawPlan, false, false))).pipe(
            switchMap((rebuiltValidatablePlan: ValidatablePlan) =>
                from(
                    convertAJSPromiseToNative(
                        this.commonPlanSelectionService.selectModule(newModule, parentModule, rawPlan, rebuiltValidatablePlan, studyRight),
                    ),
                ),
            ),
            switchMap(() => this.planEntityService.updateMyPlan(rawPlan)),
        );
    }

    /**
     * Shared core for copy-and-replace. Deep clones the source plan before any mutation, copies it into a new plan
     * (id stripped by {@link PlanCopyService.copyPlanObject}), removes the old module version and selects the new one,
     * then creates a new plan.
     */
    private copyPlanAndReplaceModuleInternal(
        oldModule: Module,
        newModule: Module,
        sourcePlan: Plan,
        sourceValidatablePlan: ValidatablePlan,
        studyRight: StudyRight,
    ): Observable<Plan> {
        const parentModule = sourceValidatablePlan.getParentModuleOrCustomModuleAttainmentForModule(oldModule);
        const newPlan = this.planCopyService.copyPlanObject(_.cloneDeep(sourcePlan));
        this.rawPlanEditService.removeModuleRecursively(oldModule.id, newPlan);
        return from(convertAJSPromiseToNative(this.commonPlanService.getValidatablePlan(newPlan, false, false))).pipe(
            switchMap((validatablePlan: ValidatablePlan) =>
                from(
                    convertAJSPromiseToNative(
                        this.commonPlanSelectionService.selectModule(newModule, parentModule, newPlan, validatablePlan, studyRight),
                    ),
                ).pipe(switchMap(() => this.planEntityService.createMyPlan(newPlan))),
            ),
        );
    }
}
